import {
  getRestorationModeStatus as apiFetch,
  forceExitRestorationMode as apiForceExit,
  retryReconciliation as apiRetry,
  RestorationModeStatus,
} from 'js/common/services/api/deployment/restorationModeApi';

import type { RootThunk } from '../rootReducer';

// ─── Action types ────────────────────────────────────────────────────────────

const SET_STATUS = 'restorationMode/SET_STATUS';
const SET_LOADING = 'restorationMode/SET_LOADING';
const SET_ERROR = 'restorationMode/SET_ERROR';

// ─── State ───────────────────────────────────────────────────────────────────

export interface RestorationModeState {
  status: RestorationModeStatus | null;
  loading: boolean;
  error: string | null;
}

const initialState: RestorationModeState = {
  status: null,
  loading: false,
  error: null,
};

// ─── Reducer ─────────────────────────────────────────────────────────────────

export default function restorationModeReducer(
  state: RestorationModeState = initialState,
  action: { type: string; payload?: any }
): RestorationModeState {
  switch (action.type) {
    case SET_STATUS:
      return { ...state, status: action.payload, loading: false, error: null };
    case SET_LOADING:
      return { ...state, loading: action.payload };
    case SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    default:
      return state;
  }
}

// ─── Selectors ───────────────────────────────────────────────────────────────

export const getStatus = (state): RestorationModeStatus | null => state.restorationMode.status;
export const isLoading = (state): boolean => state.restorationMode.loading;
export const getError = (state): string | null => state.restorationMode.error;
export const isInRestorationMode = (state): boolean => state.restorationMode.status?.restorationMode === true;
export const hasReconciliationFailed = (state): boolean =>
  isInRestorationMode(state) && state.restorationMode.status?.reconciliationFailed === true;

// ─── Thunks ──────────────────────────────────────────────────────────────────

export const fetchStatus =
  (groupId: string): RootThunk<Promise<void>> =>
  async (dispatch) => {
    dispatch({ type: SET_LOADING, payload: true });
    try {
      const status = await apiFetch(groupId);
      dispatch({ type: SET_STATUS, payload: status });
    } catch (e) {
      dispatch({ type: SET_ERROR, payload: e instanceof Error ? e.message : JSON.stringify(e) });
    }
  };

export const forceExitRestorationMode =
  (groupId: string): RootThunk<Promise<void>> =>
  async (dispatch) => {
    await apiForceExit(groupId);
    await dispatch(fetchStatus(groupId));
  };

export const retryReconciliation =
  (groupId: string): RootThunk<Promise<void>> =>
  async (dispatch) => {
    await apiRetry(groupId);
    await dispatch(fetchStatus(groupId));
  };
